import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  axiosGet,
  axiosPost,
  reactQueryDefaultOptions,
} from "../helpers/baseApi";
import { SERVICE_URLS } from "../helpers/serviceUrls";
import { QUERY_KEYS } from "../helpers/queryKeys";

// Feature Access Control: per-OE-period, per-feature client allow-list.
// Opt-in semantics - a client on the list sees the feature, everyone else
// does not.

// Feature keys available to gate, read from the server so the admin dropdown
// stays in sync with Shared/Consts/FeatureKeys.cs.
export const useQueryGetFeatureKeys = () => {
  const getData = async () => {
    return await axiosGet(SERVICE_URLS.FEATURE_ACCESS_KEYS_GET);
  };

  return useQuery([QUERY_KEYS.FEATURE_ACCESS_KEYS], getData, {
    ...reactQueryDefaultOptions,
  });
};

/**
 * Should the admin list query run?
 *
 * An empty featureKey is meaningful - it is how the page asks for every feature
 * in the period - so this deliberately does NOT use a truthiness check on
 * featureKey. Only null/undefined (nothing chosen yet) should suppress the
 * request. Exported so the distinction is covered by a test rather than
 * re-broken the next time this is touched.
 */
export const isFeatureAccessListEnabled = (oePeriodId, featureKey) =>
  !!oePeriodId && featureKey !== null && featureKey !== undefined;

// Rows for the admin grid, scoped to one OE period and optionally one feature
// key. A blank featureKey returns every feature for the period.
export const useQueryGetFeatureAccessList = (
  oePeriodId,
  featureKey,
  includeInactive = false
) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.FEATURE_ACCESS_LIST_GET(
        oePeriodId,
        featureKey,
        includeInactive
      )
    );
  };

  return useQuery(
    [QUERY_KEYS.FEATURE_ACCESS_LIST, oePeriodId, featureKey, includeInactive],
    getData,
    {
      ...reactQueryDefaultOptions,
      enabled: isFeatureAccessListEnabled(oePeriodId, featureKey),
    }
  );
};

// The read side: which gated features are enabled for one client.
export const useQueryGetEnabledFeatures = (oePeriodId, clientId) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.FEATURE_ACCESS_ENABLED_GET(oePeriodId, clientId)
    );
  };

  return useQuery(
    [QUERY_KEYS.FEATURE_ACCESS_ENABLED, oePeriodId, clientId],
    getData,
    {
      ...reactQueryDefaultOptions,
      enabled: !!oePeriodId && !!clientId,
    }
  );
};

/**
 * The union of gated features across every client this user can reach.
 *
 * For screens that list clients instead of sitting inside one, where there is no
 * single clientId to ask about. Takes no clientId - the server scopes it to the
 * caller's own claims.
 */
export const useQueryGetEnabledFeaturesForMyClients = (oePeriodId) => {
  const getData = async () => {
    return await axiosGet(SERVICE_URLS.FEATURE_ACCESS_ENABLED_ANY_GET(oePeriodId));
  };

  return useQuery([QUERY_KEYS.FEATURE_ACCESS_ENABLED_ANY, oePeriodId], getData, {
    ...reactQueryDefaultOptions,
    enabled: !!oePeriodId,
  });
};

/**
 * Distinct medical carriers on a client's rate model version.
 *
 * Used by the Aetna feature gate, which blocks submitting a model containing
 * more than one. Terminated plans are already excluded server-side, so a client
 * switching carriers is not counted twice.
 */
export const useQueryGetClientRateModelMedicalCarriers = (
  oePeriodId,
  clientId,
  versionId
) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_RATE_MODEL_MEDICAL_CARRIERS_GET(
        oePeriodId,
        clientId,
        versionId
      )
    );
  };

  return useQuery(
    [
      QUERY_KEYS.CLIENT_RATE_MODEL_MEDICAL_CARRIERS,
      oePeriodId,
      clientId,
      versionId,
    ],
    getData,
    {
      ...reactQueryDefaultOptions,
      enabled: !!oePeriodId && !!clientId && !!versionId,
    }
  );
};

/**
 * May this rate model version be submitted, given the client's medical carriers
 * and their carrier feature keys?
 *
 * The verdict is computed server side rather than assembled here: the rule keys
 * off carrier ids that differ per environment, and the same evaluator backs the
 * submit endpoint, so the button and the API cannot disagree.
 *
 * Shape: { isAllowed, decidingFeatureKey, message, carrierNames }. A client on
 * no carrier rule gets isAllowed = true.
 */
export const useQueryGetClientRateModelSubmitCarrierRule = (
  oePeriodId,
  clientId,
  versionId
) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_RATE_MODEL_SUBMIT_CARRIER_RULE_GET(
        oePeriodId,
        clientId,
        versionId
      )
    );
  };

  return useQuery(
    [
      QUERY_KEYS.CLIENT_RATE_MODEL_SUBMIT_CARRIER_RULE,
      oePeriodId,
      clientId,
      versionId,
    ],
    getData,
    {
      ...reactQueryDefaultOptions,
      enabled: !!oePeriodId && !!clientId && !!versionId,
    }
  );
};

/**
 * Does a submit-carrier-rule query say this model is blocked?
 *
 * Fails safe to false - allowing the submit - while loading, on error, and on an
 * unexpected response shape, matching hasEnabledFeature. A broken lookup must not
 * block every client from submitting.
 */
export const isBlockedBySubmitCarrierRule = ({ data, isLoading, isError }) => {
  if (isLoading || isError) {
    return false;
  }

  return data?.data?.isAllowed === false;
};

const invalidateFeatureAccess = (queryClient, variables) => {
  // A bulk add can touch several feature keys at once, so invalidate every
  // list query for the period rather than one (period, feature) pair.
  queryClient.invalidateQueries([
    QUERY_KEYS.FEATURE_ACCESS_LIST,
    variables?.oePeriodId,
  ]);
  queryClient.invalidateQueries([QUERY_KEYS.FEATURE_ACCESS_ENABLED]);
  queryClient.invalidateQueries([QUERY_KEYS.FEATURE_ACCESS_ENABLED_ANY]);
  // New keys become selectable options, so the dropdown source is stale too.
  queryClient.invalidateQueries([QUERY_KEYS.FEATURE_ACCESS_KEYS]);
};

// Bulk add / reactivate. Used by both the manual add and the CSV import.
export const useMutatePostFeatureAccessClients = () => {
  const postData = async (data) => {
    return await axiosPost(SERVICE_URLS.FEATURE_ACCESS_CLIENTS_POST, data);
  };

  const queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: (result, variables) =>
      invalidateFeatureAccess(queryClient, variables),
  });
};

// Moves one client grant to a different feature, backing the editable Feature
// column. Resolves with { updated, featureKey, errorMessage } - a rejected
// change returns updated: false rather than throwing.
export const useMutatePostFeatureAccessUpdateKey = () => {
  const postData = async (data) => {
    return await axiosPost(SERVICE_URLS.FEATURE_ACCESS_UPDATE_KEY_POST, data);
  };

  const queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: (result, variables) =>
      invalidateFeatureAccess(queryClient, variables),
  });
};

export const useMutatePostFeatureAccessRemove = () => {
  const postData = async (data) => {
    return await axiosPost(SERVICE_URLS.FEATURE_ACCESS_REMOVE_POST, data);
  };

  const queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: (result, variables) =>
      invalidateFeatureAccess(queryClient, variables),
  });
};

export const useMutatePostFeatureAccessRemoveAll = () => {
  const postData = async (data) => {
    return await axiosPost(SERVICE_URLS.FEATURE_ACCESS_REMOVE_ALL_POST, data);
  };

  const queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: (result, variables) =>
      invalidateFeatureAccess(queryClient, variables),
  });
};
